import { Content, Group, Item, ItemIndicator, ItemText, Label, Portal, Root, ScrollDownButton, ScrollUpButton, Separator, Trigger, Value, Viewport } from '@radix-ui/react-select';
import { type ComponentPropsWithoutRef, type ElementRef, forwardRef } from 'react';
import { cn } from '@/lib/cn';
import Icon from '../Icon/Icon';
const Select = Root;
const SelectGroup = Group;
const SelectValue = Value;
const SelectTrigger = forwardRef<ElementRef<typeof Trigger>, ComponentPropsWithoutRef<typeof Trigger>>(({
  className,
  children,
  ...props
}, ref) => <Trigger ref={ref} className={cn('relative flex h-9 w-full items-center justify-start text-start whitespace-nowrap rounded-md border border-neutral-muted-border bg-neutral-fill px-3 py-2 pr-10 text-sm shadow-xs ring-offset-background placeholder:text-neutral-on-muted-fill focus:outline-hidden focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50', className)} {...props}>
    {children}
    <div className="absolute inset-y-0 right-3 flex items-center">
      <Icon iconName="ChevronDownIcon" className="shrink-0 opacity-50" />
    </div>
  </Trigger>);
SelectTrigger.displayName = Trigger.displayName;
const SelectScrollUpButton = forwardRef<ElementRef<typeof ScrollUpButton>, ComponentPropsWithoutRef<typeof ScrollUpButton>>(({
  className,
  ...props
}, ref) => <ScrollUpButton ref={ref} className={cn('flex cursor-default items-center justify-center py-1', className)} {...props}>
    <Icon iconName="ChevronUpIcon" />
  </ScrollUpButton>);
SelectScrollUpButton.displayName = ScrollUpButton.displayName;
const SelectScrollDownButton = forwardRef<ElementRef<typeof ScrollDownButton>, ComponentPropsWithoutRef<typeof ScrollDownButton>>(({
  className,
  ...props
}, ref) => <ScrollDownButton ref={ref} className={cn('flex cursor-default items-center justify-center py-1', className)} {...props}>
    <Icon iconName="ChevronDownIcon" />
  </ScrollDownButton>);
SelectScrollDownButton.displayName = ScrollDownButton.displayName;
const SelectContent = forwardRef<ElementRef<typeof Content>, ComponentPropsWithoutRef<typeof Content>>(({
  className,
  children,
  position = 'popper',
  ...props
}, ref) => <Portal>
    <Content ref={ref} className={cn('relative z-50 max-h-96 min-w-32 overflow-hidden rounded-md border border-neutral-muted-border bg-neutral-fill text-neutral-on-fill shadow-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2', position === 'popper' && 'data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1', className)} position={position} {...props}>
      <SelectScrollUpButton />
      <Viewport className={cn('p-1', position === 'popper' && 'h-(--radix-select-trigger-height) w-full min-w-(--radix-select-trigger-width)')}>
        {children}
      </Viewport>
      <SelectScrollDownButton />
    </Content>
  </Portal>);
SelectContent.displayName = Content.displayName;
const SelectLabel = forwardRef<ElementRef<typeof Label>, ComponentPropsWithoutRef<typeof Label>>(({
  className,
  ...props
}, ref) => <Label ref={ref} className={cn('px-2 py-1.5 text-sm font-semibold', className)} {...props} />);
SelectLabel.displayName = Label.displayName;
const SelectItem = forwardRef<ElementRef<typeof Item>, ComponentPropsWithoutRef<typeof Item>>(({
  className,
  children,
  ...props
}, ref) => <Item ref={ref} className={cn('relative flex w-full cursor-default select-none items-center rounded-sm py-1.5 pl-2 pr-8 text-sm outline-hidden focus:bg-primary-muted-fill focus:text-primary-on-muted-fill data-disabled:pointer-events-none data-disabled:opacity-50', className)} {...props}>
    <span className="absolute right-2 flex h-3.5 w-3.5 items-center justify-center">
      <ItemIndicator>
        <Icon iconName="Check" />
      </ItemIndicator>
    </span>
    <ItemText>{children}</ItemText>
  </Item>);
SelectItem.displayName = Item.displayName;
const SelectSeparator = forwardRef<ElementRef<typeof Separator>, ComponentPropsWithoutRef<typeof Separator>>(({
  className,
  ...props
}, ref) => <Separator ref={ref} className={cn('-mx-1 my-1 h-px bg-muted', className)} {...props} />);
SelectSeparator.displayName = Separator.displayName;
export { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue };